import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  checkCoupon,
  createBooking,
  createRazorpayOrder,
  fetchAddresses,
  fetchCoupons,
} from "../../services/api";
import { CART_KEY, PAYMENT_MODE } from "../../utils/constants";
import { notifyCartUpdated } from "../../utils/cart";
import { openRazorpayCheckout } from "../../utils/razorpay";
import { toErrorMessage } from "../../utils/errorMessage";
import Loader from "../common/Loader";
import "../common/Account.css";

const formatBookingDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};

const getTodayInputDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Backend stores bookingTime via moment().format("h:mm:ss a") — e.g. "11:00:00 am".
// Bare "11:00 AM" fails moment(rawTime) and can be saved as "Invalid date".
const formatHourLabel = (hour) => {
  const period = hour >= 12 ? "pm" : "am";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00:00 ${period}`;
};

const TIME_SLOTS = Array.from({ length: 24 }, (_, hour) => formatHourLabel(hour));

const getDefaultTimeSlot = () => {
  const hour = new Date().getHours();
  return TIME_SLOTS[hour] || TIME_SLOTS[0];
};

const Checkout = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const [cart, setCart] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [form, setForm] = useState({
    bookingDate: getTodayInputDate(),
    bookingTime: getDefaultTimeSlot(),
    paymentMode: String(PAYMENT_MODE.ONLINE),
    addressId: "",
  });

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    setCart(items);
    const init = async () => {
      setLoading(true);
      try {
        const [addrRes, couponRes] = await Promise.all([
          fetchAddresses().catch(() => ({ data: [] })),
          fetchCoupons().catch(() => ({ data: [] })),
        ]);
        setAddresses(Array.isArray(addrRes?.data) ? addrRes.data : []);
        setCoupons(Array.isArray(couponRes?.data) ? couponRes.data : []);
      } catch (err) {
        setError(toErrorMessage(err, "Failed to load checkout data"));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const amount = useMemo(
    () =>
      cart.reduce(
        (sum, item) => sum + Number(item.amount || item.price || 0),
        0
      ),
    [cart]
  );

  const discountAmount = useMemo(() => {
    if (!appliedCoupon?.discountPercentage) return 0;
    return Math.round(
      (amount * Number(appliedCoupon.discountPercentage)) / 100
    );
  }, [amount, appliedCoupon]);

  const finalAmount = Math.max(amount - discountAmount, 0);

  const removeItem = (id) => {
    const next = cart.filter((c) => c._id !== id);
    setCart(next);
    localStorage.setItem(CART_KEY, JSON.stringify(next));
    notifyCartUpdated({ count: next.length });
  };

  const applyCoupon = async (codeOverride) => {
    const code = String(codeOverride ?? couponCode).trim();
    if (!code) {
      setError("Enter a coupon code");
      return;
    }

    setCouponCode(code);
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      // Match against already-loaded coupons first (case-insensitive)
      let coupon =
        coupons.find(
          (c) =>
            String(c?.name || "").toLowerCase() === code.toLowerCase() &&
            Number(c?.isActive) === 1 &&
            Number(c?.isDeleted) !== 1
        ) || null;

      if (!coupon) {
        const res = await checkCoupon(code);
        coupon = res?.data || null;
      }

      if (!coupon) {
        setAppliedCoupon(null);
        setError("Invalid coupon code");
        return;
      }

      const minAmount = Number(coupon.minAmount || 0);
      if (minAmount && amount < minAmount) {
        setAppliedCoupon(null);
        setError(`Minimum cart amount ₹${minAmount} required for ${coupon.name}`);
        return;
      }

      setAppliedCoupon(coupon);
      setSuccess(
        `Coupon ${coupon.name} applied — ${coupon.discountPercentage}% off`
      );
    } catch (err) {
      setError(toErrorMessage(err, "Coupon check failed"));
      setAppliedCoupon(null);
    } finally {
      setSubmitting(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setSuccess("");
    setError("");
  };

  const placeBooking = async (paymentInfo = {}) => {
    const testIds = [];
    const packageId = [];

    cart.forEach((item) => {
      if (!item?._id) return;
      if (item.isPackage || item.itemType === "package") {
        packageId.push(String(item._id));
      } else {
        testIds.push(String(item._id));
      }
    });

    // Fallback: if nothing classified, treat all cart items as tests
    if (!testIds.length && !packageId.length) {
      cart.forEach((item) => {
        if (item?._id) testIds.push(String(item._id));
      });
    }

    if (!testIds.length && !packageId.length) {
      throw new Error("No valid tests/packages found in cart");
    }

    if (!form.bookingDate || !form.bookingTime) {
      throw new Error("Booking date and time are required");
    }

    if (!paymentInfo.transactionId) {
      throw new Error("Payment transaction id missing after Razorpay success");
    }

    const payload = {
      testIds,
      packageId,
      amount: Number(amount) || 0,
      finalAmount: Number(finalAmount) || 0,
      discountAmount: Number(discountAmount) || 0,
      bookingDate: formatBookingDate(form.bookingDate), // DD/MM/YYYY
      bookingTime: String(form.bookingTime),
      paymentMode: PAYMENT_MODE.ONLINE, // 2
      transactionId: String(paymentInfo.transactionId),
      prescriptionLink: "",
    };

    if (form.addressId) {
      payload.addressId = String(form.addressId);
    }

    if (appliedCoupon?.name) {
      payload.couponCode = appliedCoupon.name;
      payload.couponName = appliedCoupon.name;
    }

    // Keep Razorpay meta if backend wants it later
    if (paymentInfo.razorpay_order_id) {
      payload.razorpayOrderId = paymentInfo.razorpay_order_id;
    }
    if (paymentInfo.razorpay_signature) {
      payload.razorpaySignature = paymentInfo.razorpay_signature;
    }

    console.log("Creating booking with payload:", payload);

    const res = await createBooking(payload);

    if (!res || res.status === 0) {
      throw new Error(toErrorMessage(res, "Booking failed after payment"));
    }

    localStorage.setItem(CART_KEY, "[]");
    setCart([]);
    notifyCartUpdated({ count: 0 });
    setSuccess("Payment successful. Booking created.");
    const id = res?.data?._id;
    navigate(id ? `/my-bookings/${id}` : "/my-bookings");
    return res;
  };

  const openOnlinePayment = async () => {
    if (finalAmount <= 0) {
      throw new Error("Payable amount must be greater than 0");
    }

    // Backend expects amount in rupees and converts to paise
    const orderRes = await createRazorpayOrder(finalAmount, "INR");

    const payment = await openRazorpayCheckout({
      orderPayload: orderRes,
      amountInRupees: finalAmount,
      user,
      description: `Kior Healthcare booking · ₹${finalAmount}`,
      notes: {
        bookingDate: formatBookingDate(form.bookingDate),
        bookingTime: form.bookingTime,
        userId: user?._id || user?.id || "",
        mobileNumber: user?.mobileNumber || "",
        itemCount: String(cart.length),
      },
    });

    await placeBooking({
      transactionId: payment.razorpay_payment_id,
      razorpay_order_id: payment.razorpay_order_id,
      razorpay_signature: payment.razorpay_signature,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!cart.length) {
      setError("Your cart is empty");
      return;
    }
    if (!form.bookingDate || !form.bookingTime) {
      setError("Select booking date and time");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await openOnlinePayment();
    } catch (err) {
      setError(toErrorMessage(err, "Payment or booking failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="account-page">
        <Loader fullPage message="Loading checkout..." />
      </div>
    );
  }

  return (
    <div className="account-page checkout-page">
      {submitting && (
        <Loader overlay message="Opening online payment..." />
      )}
      <div className="coustom_container">
        <h1>Cart & Checkout</h1>
        {error && (
          <Alert variant="danger">
            {typeof error === "string" ? error : toErrorMessage(error)}
          </Alert>
        )}
        {success && <Alert variant="success">{String(success)}</Alert>}

        <Form onSubmit={onSubmit}>
          <Row className="gy-4">
            <Col lg={7}>
              <div className="account-card mb-3">
                <h2 className="h5">Cart items</h2>
                {!cart.length && (
                  <p className="text-muted mb-0">
                    Cart is empty.{" "}
                    <Link to="/laboratory-tests-mohali">Browse tests</Link>
                  </p>
                )}
                {cart.map((item) => (
                  <div key={item._id} className="booking-item">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <h5>{item.name}</h5>
                        <p className="mb-0 fw-semibold text-success">
                          ₹{item.amount || item.price || 0}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        type="button"
                        onClick={() => removeItem(item._id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="account-card mb-3">
                <h2 className="h5">Booking details</h2>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Booking date</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.bookingDate}
                        min={getTodayInputDate()}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            bookingDate: e.target.value,
                          }))
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Time</Form.Label>
                      <Form.Select
                        value={form.bookingTime}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            bookingTime: e.target.value,
                          }))
                        }
                        required
                      >
                        {TIME_SLOTS.map((slot) => (
                          <option key={slot} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="account-card mb-3">
                <h2 className="h5">Payment method</h2>
                <div className="payment-options">
                  <div className="payment-option active">
                    <strong>Online payment</strong>
                    <span>Pay securely with UPI / Card / Netbanking</span>
                  </div>
                </div>
              </div>

              <div className="account-card">
                <h2 className="h5">Collection address (optional)</h2>
                <Form.Select
                  value={form.addressId}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, addressId: e.target.value }))
                  }
                >
                  <option value="">Centre visit / none</option>
                  {addresses.map((a) => (
                    <option key={a._id} value={a._id}>
                      {[a.houseNumber, a.address, a.locality]
                        .filter(Boolean)
                        .join(", ")}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text>
                  Manage addresses from <Link to="/addresses">My Addresses</Link>
                </Form.Text>
              </div>
            </Col>

            <Col lg={5}>
              <div className="checkout-sidebar">
                <div className="account-card mb-3">
                  <h2 className="h5">Coupon</h2>
                  <div className="coupon-row">
                    <Form.Control
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter code e.g. KIOR10"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyCoupon();
                        }
                      }}
                    />
                    <Button
                      className="btn-kior"
                      type="button"
                      onClick={() => applyCoupon()}
                      disabled={submitting}
                    >
                      Apply
                    </Button>
                  </div>

                  {appliedCoupon && (
                    <div className="applied-coupon mt-2">
                      <span>
                        Applied: <strong>{appliedCoupon.name}</strong> (
                        {appliedCoupon.discountPercentage}% off)
                      </span>
                      <button type="button" onClick={removeCoupon}>
                        Remove
                      </button>
                    </div>
                  )}

                  {coupons.length > 0 && (
                    <div className="coupon-chips mt-2">
                      {coupons.slice(0, 5).map((c) => (
                        <button
                          key={c._id}
                          type="button"
                          className={`coupon-chip ${
                            appliedCoupon?.name === c.name ? "active" : ""
                          }`}
                          onClick={() => applyCoupon(c.name)}
                        >
                          {c.name} — {c.discountPercentage}% off
                          {c.minAmount ? ` (min ₹${c.minAmount})` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="account-card summary-card">
                  <h2 className="h5">Summary</h2>
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₹{amount}</span>
                  </div>
                  <div className="summary-row">
                    <span>Discount</span>
                    <span>₹{discountAmount}</span>
                  </div>
                  <div className="summary-row">
                    <span>Payment</span>
                    <span>Online</span>
                  </div>
                  <div className="summary-row summary-total">
                    <span>Payable</span>
                    <span>₹{finalAmount}</span>
                  </div>

                  <Button
                    type="submit"
                    className="btn-kior w-100 mt-3"
                    disabled={!cart.length || submitting}
                  >
                    {`Pay online · ₹${finalAmount}`}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default Checkout;
